import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Loader2, Wand2, Check, TrendingUp, AlertTriangle, Zap, Bookmark, Trash2, Save, GitCompare } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const SCORES = ["readiness", "sharpness", "effort", "rest"];
const DEPTHS = [
  { id: "fast", label: "Fast", detail: "~12s · ~1.2k plans simulated", credits: 15 },
  { id: "deep", label: "Deep", detail: "~45s · ~5k plans simulated", credits: 40 },
  { id: "deepest", label: "Deepest", detail: "~2.5min · ~20k plans simulated", credits: 90 },
  { id: "exhaustive", label: "1M", detail: "~5min · 1,000,000 plans screened, best 2,500 fully simulated", credits: 250 },
];
const MODES = [
  { id: "peak", label: "Personal Peak", blurb: "Fastest route to their own ceiling on all four scores" },
  { id: "readiness", label: "Peak Readiness", blurb: "Highest readiness on the match day" },
  { id: "sharpness", label: "Peak Sharpness", blurb: "Sharpest on the match day, skill work protected" },
  { id: "balance", label: "Best Balance", blurb: "Best combined readiness, sharpness and rest on the match day" },
];
const STAGES = [
  ["learning", "Learning this athlete's response weights"],
  ["ceiling", "Estimating their personal ceiling"],
  ["screening", "Screening the candidate space analytically"],
  ["searching", "Simulating candidate weekly plans day by day"],
  ["verifying", "Verifying the winning plan day by day"],
];

function Bar({ pct }) {
  return (
    <div className="h-1.5 w-full bg-surface border border-border overflow-hidden">
      <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

function CeilingCard({ k, c }) {
  const pct = Math.min(100, Math.round((c.current / Math.max(1, c.ceiling)) * 100));
  return (
    <div className="border border-border p-4" data-testid={`peak-ceiling-${k}`}>
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">{k}</span>
        {c.provisional && <span className="text-[9px] uppercase tracking-wider border border-traffic-amber text-traffic-amber px-1">provisional</span>}
      </div>
      <div className="font-heading text-3xl font-black tabular-nums mt-1">{c.ceiling}</div>
      <div className="text-[11px] text-muted-foreground tabular-nums">range {c.lo}–{c.hi} · now {c.current} · best ever {c.best_observed}</div>
      <div className="mt-2"><Bar pct={pct} /></div>
      <div className="text-[11px] text-muted-foreground mt-1.5">{c.confidence}% confidence · {c.samples} days of data</div>
      <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{c.rationale}</p>
    </div>
  );
}

export default function PeakEngine({ athleteId, squad, academyId="", squadMode = false, onApplied, targetDate, guided=false, initialJobId="" }) {
  const [depth, setDepth] = useState(guided?'deepest':'deep');
  const [mode, setMode] = useState(guided?'readiness':'peak');
  const [target, setTarget] = useState(targetDate || new Date(Date.now() + 28 * 864e5).toISOString().slice(0, 10));
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [pick, setPick] = useState(0);
  const [applying, setApplying] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [armed, setArmed] = useState(false);
  useEffect(() => { setArmed(false); }, [pick]);
  const [saved, setSaved] = useState([]);
  const [savedOpen, setSavedOpen] = useState(false);
  const [cmpSel, setCmpSel] = useState([]);
  const [cmp, setCmp] = useState(null);
  const poll = useRef(null);

  const loadSaved = () => api.get(`/peak-engine/saved?${new URLSearchParams({squad:squad || '',academy_id:academyId})}`).then((r) => setSaved(r.data.saved || [])).catch(() => setSaved([]));
  useEffect(() => { loadSaved(); }, []);
  useEffect(() => () => clearInterval(poll.current), []);
  useEffect(() => { if (targetDate) setTarget(targetDate); }, [targetDate]);
  const modeMeta = MODES.find((m) => m.id === mode) || MODES[0];

  const saveRun = async () => {
    if (!job?.job_id) return;
    const name = window.prompt("Name this run", `${job.mode_label} · ${new Date().toLocaleDateString()}`);
    if (name === null) return;
    try {
      await api.post(`/peak-engine/jobs/${job.job_id}/save`, { name });
      toast.success("Run saved", { description: "Reopen it any time from Saved runs · no credits" });
      loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const openSaved = async (s) => {
    setMode(s.mode || "peak");
    setDepth(s.depth || "deep");
    if (s.target_date) setTarget(s.target_date);
    try {
      const j = await api.get(`/peak-engine/jobs/${s.job_id}`);
      setJob(j.data); setPick(0); setShowDiff(false); setArmed(false); setSavedOpen(false);
      toast.success("Saved run reopened", { description: "No credits used · same plans as when it ran" });
    } catch (e) { toast.error("That run is no longer available"); }
  };

  const applySettings = (s) => {
    setMode(s.mode || "peak");
    setDepth(s.depth || "deep");
    if (s.target_date) setTarget(s.target_date);
    setSavedOpen(false);
    toast.message("Options loaded", { description: `${s.mode_label} · ${s.depth_label}` });
  };

  const removeSaved = async (s) => {
    try { await api.delete(`/peak-engine/saved/${s.saved_id}`); loadSaved(); } catch { /* ignore */ }
  };

  useEffect(() => { if (initialJobId) api.get(`/peak-engine/jobs/${initialJobId}`).then((j) => { setJob(j.data); if (j.data.mode) setMode(j.data.mode); if (j.data.target_date) setTarget(j.data.target_date); }).catch(() => toast.error("That run is no longer available")); }, [initialJobId]);

  const toggleCmp = (id) => setCmpSel((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id].slice(-2));

  const compare = async () => {
    if (cmpSel.length !== 2) return;
    try {
      const { data } = await api.post("/peak-engine/compare", { saved_ids: cmpSel });
      setCmp(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not compare those runs"); }
  };

  const start = async () => {
    setRunning(true); setJob(null); setPick(0); setShowDiff(false); setArmed(false);
    try {
      const body = { depth, mode, ...(mode === "peak" ? {} : { target_date: target }),
        ...(squadMode ? { squad: squad || undefined,academy_id:academyId } : { athlete_id: athleteId }) };
      const r = await api.post("/peak-engine/run", body);
      toast.message(`Peak Engine running · ${r.data.athletes} athlete(s)`, { description: `Up to ${r.data.credits_max} credits, charged in quarters as it runs · ~${r.data.expected_seconds}s` });
      clearInterval(poll.current);
      poll.current = setInterval(async () => {
        try {
          const j = await api.get(`/peak-engine/jobs/${r.data.job_id}`);
          setJob(j.data);
          if (j.data.status === "done" || j.data.status === "error") { clearInterval(poll.current); setRunning(false); }
        } catch (e) { clearInterval(poll.current); setRunning(false); }
      }, 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Peak Engine");
      setRunning(false);
    }
  };

  const results = (job?.results || []).filter((r) => r && !r.error);
  const errors = (job?.results || []).filter((r) => r?.error);
  const res = results[pick];

  const apply = async (aid) => {
    const id = aid || res?.athlete_id;
    if (!id) return;
    if (!armed) { setShowDiff(true); setArmed(true); return; }
    setArmed(false);
    setApplying(true);
    try {
      const r = await api.post(`/peak-engine/jobs/${job.job_id}/apply?athlete_id=${id}`);
      toast.success(`Applied · ${r.data.inserted} sessions written`, { description: `${r.data.replaced_drafts} old draft(s) replaced` });
      onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setApplying(false);
  };

  const d = job?.detail || {};
  const seen = job?.stages_seen || [];
  const stageIdx = STAGES.findIndex(([s]) => s === d.stage);

  const chart = res ? (res.trajectory || []).map((p, i) => ({
    date: p.date,
    peak: p.readiness, peakRest: p.rest,
    current: res.current_trajectory?.[i]?.readiness ?? null,
  })) : [];

  return (
    <div className="border border-foreground" data-testid="peak-engine">
      <div className="px-4 py-3 border-b border-border space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Zap className="w-4 h-4" />
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-heading font-bold tracking-tight text-sm">
              {squadMode ? "Team Peak Engine" : "Peak Engine"} · scenario modelling
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Models historical responses and compares candidate schedules against your target date. Estimates are unvalidated; screened plans and fully simulated plans are counted separately.
            </p>
          </div>
          <div className="inline-flex border border-border overflow-hidden" data-testid="peak-depth">
            {DEPTHS.filter(x=>!guided||['deepest','exhaustive'].includes(x.id)).map((x) => (
              <button key={x.id} data-testid={`peak-depth-${x.id}`} onClick={() => setDepth(x.id)} disabled={running}
                title={`${x.detail} · ${x.credits} credits${squadMode ? " per athlete" : ""}`}
                className={`px-3 py-1.5 text-xs transition-colors ${depth === x.id ? "bg-foreground text-background" : "hover:bg-surface"} disabled:opacity-50`}>
                {x.label}
              </button>
            ))}
          </div>
          <button data-testid="peak-run" onClick={start} disabled={running || (squadMode ? !squad : !athleteId)}
            className="text-xs px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center gap-2">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {running ? "Working" : squadMode ? `Run for this team · ${modeMeta.label}` : `Run ${modeMeta.label}`}
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="peak-modes">
          {MODES.filter(m=>!guided||m.id!=='peak').map((m) => (
            <button key={m.id} data-testid={`peak-mode-${m.id}`} onClick={() => setMode(m.id)} disabled={running}
              title={m.blurb}
              className={`px-3 py-1.5 text-xs border transition-colors ${mode === m.id ? "bg-foreground text-background border-foreground" : "border-border hover:bg-surface"} disabled:opacity-50`}>
              {m.label}
            </button>
          ))}
          {mode !== "peak" && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
              Match day
              <input type="date" data-testid="peak-target-date" value={target} onChange={(e) => setTarget(e.target.value)}
                disabled={running} className="border border-border bg-background px-2 py-1 text-xs" />
            </label>
          )}
          <button data-testid="peak-saved-toggle" onClick={() => setSavedOpen((o) => !o)}
            title="Reopen a run you already paid for, or reuse its settings"
            className="ml-auto text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5">
            <Bookmark className="w-3.5 h-3.5" /> Saved runs{saved.length ? ` (${saved.length})` : ""}
          </button>
        </div>
        {savedOpen && (
          <div className="border border-border" data-testid="peak-saved-list">
            {saved.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">
                Nothing saved yet · finish a run and tap Save this run. Reopening a saved run costs no credits.
              </p>
            ) : saved.map((s) => (
              <div key={s.saved_id} className="p-3 border-b border-border last:border-0 flex items-center gap-3 flex-wrap"
                data-testid={`peak-saved-${s.saved_id}`}>
                <input type="checkbox" checked={cmpSel.includes(s.saved_id)} onChange={() => toggleCmp(s.saved_id)}
                  data-testid={`peak-cmp-select-${s.saved_id}`} title="Pick two runs to compare" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{s.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {s.mode_label} · {s.depth_label} · {s.athlete_count} athlete(s)
                    {s.target_date ? ` · match day ${s.target_date}` : ""} · run {String(s.run_at || "").slice(0, 10)}
                  </p>
                </div>
                <button data-testid={`peak-saved-open-${s.saved_id}`} onClick={() => openSaved(s)}
                  className="text-xs px-3 py-1.5 bg-foreground text-background hover:bg-neutral-800 transition-colors">Open · free</button>
                <button data-testid={`peak-saved-options-${s.saved_id}`} onClick={() => applySettings(s)}
                  className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">Use settings</button>
                <button data-testid={`peak-saved-del-${s.saved_id}`} onClick={() => removeSaved(s)} aria-label="Delete"
                  className="text-muted-foreground hover:text-traffic-red transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {saved.length > 1 && (
              <div className="p-3 border-t border-border flex items-center gap-3 flex-wrap">
                <span className="text-[11px] text-muted-foreground flex-1">
                  Tick two runs to see them side by side · comparing costs nothing.
                </span>
                <button data-testid="peak-compare-btn" onClick={compare} disabled={cmpSel.length !== 2}
                  className="text-xs px-3 py-1.5 border border-foreground hover:bg-surface transition-colors disabled:opacity-40 flex items-center gap-1.5">
                  <GitCompare className="w-3.5 h-3.5" /> Compare {cmpSel.length}/2
                </button>
              </div>
            )}
          </div>
        )}
        {cmp && (
          <div className="border border-foreground" data-testid="peak-compare">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold text-sm flex-1">Run comparison · {cmp.runs[0].name} vs {cmp.runs[1].name}</span>
              <button data-testid="peak-compare-close" onClick={() => setCmp(null)}
                className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">Close</button>
            </div>
            <p className="px-4 py-2 text-sm border-b border-border" data-testid="peak-compare-verdict">{cmp.verdict}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2">{cmp.runs[0].athletes > 1 ? "Team average" : "This athlete"}</th>
                    <th className="text-right px-3 py-2">{cmp.runs[0].name}</th>
                    <th className="text-right px-3 py-2">{cmp.runs[1].name}</th>
                    <th className="text-right px-4 py-2">Δ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {cmp.metrics.map((m) => (
                    <tr key={m.key} data-testid={`peak-compare-${m.key}`}>
                      <td className="px-4 py-2">{m.label}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${m.winner === "a" ? "font-bold text-traffic-green" : ""}`}>{m.a ?? "—"}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${m.winner === "b" ? "font-bold text-traffic-green" : ""}`}>{m.b ?? "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{m.delta != null ? (m.delta > 0 ? `+${m.delta}` : m.delta) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border">{cmp.note}</p>
          </div>
        )}
      </div>

      {/* ---------------- loading screen ---------------- */}
      {running && (
        <div className="p-6 space-y-5" data-testid="peak-loading">
          <div>
            <div className="flex items-baseline justify-between text-xs text-muted-foreground mb-2">
              <span>{d.message || "Starting the model"}{job?.current_name ? ` · ${job.current_name}` : ""}</span>
              <span className="tabular-nums">{Math.round(job?.progress || 0)}%</span>
            </div>
            <Bar pct={job?.progress || 2} />
          </div>
          <ol className="space-y-2" data-testid="peak-stages">
            {STAGES.map(([s, label], i) => {
              const state = stageIdx > i || (seen.includes(s) && stageIdx !== i) ? "done" : stageIdx === i ? "active" : "todo";
              return (
                <li key={s} className="flex items-center gap-3 text-sm" data-testid={`peak-stage-${s}`}>
                  <span className={`w-5 h-5 border flex items-center justify-center text-[10px] ${state === "done" ? "bg-foreground text-background border-foreground" : state === "active" ? "border-foreground" : "border-border text-muted-foreground"}`}>
                    {state === "done" ? <Check className="w-3 h-3" /> : state === "active" ? <Loader2 className="w-3 h-3 animate-spin" /> : i + 1}
                  </span>
                  <span className={state === "todo" ? "text-muted-foreground" : ""}>{label}</span>
                </li>
              );
            })}
          </ol>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Plans tried</div><div className="font-heading text-xl font-black tabular-nums" data-testid="peak-evals">{d.evaluations || 0}</div></div>
            <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Best so far</div><div className="font-heading text-xl font-black tabular-nums">{d.best_pct != null ? `${d.best_pct}%` : "—"}</div></div>
            <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Days to peak</div><div className="font-heading text-xl font-black tabular-nums">{d.best_days_to_peak ?? "—"}</div></div>
            <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Elapsed</div><div className="font-heading text-xl font-black tabular-nums">{d.elapsed_sec || 0}s</div></div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            This is real compute, not a lookup. Every candidate plan is simulated day by day against this athlete's own fitted model.
          </p>
        </div>
      )}

      {/* ---------------- results ---------------- */}
      {!running && res && (
        <div className="p-4 space-y-6" data-testid="peak-results">
          {squadMode && results.length > 0 && (() => {
            const avg = (fn) => {
              const v = results.map(fn).filter((x) => x != null);
              return v.length ? Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10 : null;
            };
            return (
              <div className="border border-foreground" data-testid="peak-squad-rollup">
                <div className="px-4 py-2.5 border-b border-border font-heading font-bold text-sm">
                  Team roll-up · {results.length} athlete(s) on the {results[0].mode_label} plan
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-2">Team average</th>
                        {SCORES.map((k) => <th key={k} className="text-right px-3 py-2 capitalize">{k}</th>)}
                        <th className="text-right px-4 py-2">Days to peak</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr data-testid="peak-rollup-current">
                        <td className="px-4 py-2 text-muted-foreground">Current calendar</td>
                        {SCORES.map((k) => <td key={k} className="px-3 py-2 text-right tabular-nums">{avg((r) => r.current_plan?.final_scores?.[k]) ?? "—"}</td>)}
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">—</td>
                      </tr>
                      <tr data-testid="peak-rollup-plan">
                        <td className="px-4 py-2 font-medium">{results[0].mode_label} plan</td>
                        {SCORES.map((k) => <td key={k} className="px-3 py-2 text-right tabular-nums font-bold">{avg((r) => r.peak_scores?.[k]) ?? "—"}</td>)}
                        <td className="px-4 py-2 text-right tabular-nums font-bold">{avg((r) => r.days_to_peak) ?? "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="overflow-x-auto border-t border-border">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-2">Athlete</th>
                        <th className="text-right px-3 py-2">Days to peak</th>
                        <th className="text-right px-3 py-2">% of ceiling</th>
                        <th className="text-right px-3 py-2">Readiness</th>
                        <th className="text-right px-3 py-2">Risk</th>
                        <th className="text-right px-4 py-2">Plan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {results.map((r, i) => (
                        <tr key={r.athlete_id} className={i === pick ? "bg-surface" : ""} data-testid={`peak-rollup-${r.athlete_id}`}>
                          <td className="px-4 py-2">
                            <button onClick={() => setPick(i)} className="hover:underline">{r.athlete_name}</button>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.days_to_peak}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.pct_of_ceiling}%</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.peak_scores?.readiness ?? "—"}
                            <span className="text-muted-foreground"> from {r.current_plan?.final_scores?.readiness ?? "—"}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.mean_risk}%</td>
                          <td className="px-4 py-2 text-right">
                            <button data-testid={`peak-apply-${r.athlete_id}`} onClick={() => apply(r.athlete_id)} disabled={applying}
                              className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors disabled:opacity-50">Apply</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {errors.length > 0 && (
                  <div className="px-4 py-2 border-t border-border text-xs text-traffic-red">
                    {errors.length} athlete(s) could not be modelled · {errors.map((e) => e.athlete_name || e.athlete_id).join(", ")}
                  </div>
                )}
              </div>
            );
          })()}

          {results.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="peak-athlete-picker">
              {results.map((r, i) => (
                <button key={r.athlete_id} onClick={() => setPick(i)} data-testid={`peak-athlete-${r.athlete_id}`}
                  className={`px-3 py-1.5 text-xs border transition-colors ${i === pick ? "bg-foreground text-background border-foreground" : "border-border hover:bg-surface"}`}>
                  {r.athlete_name} · {r.days_to_peak}d
                </button>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="border border-foreground p-4" data-testid="peak-headline">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                {res.target_day ? `${res.mode_label} on match day` : "Days to personal peak"}
              </div>
              {res.target_day ? (
                <>
                  <div className="font-heading text-4xl font-black tabular-nums">
                    {res.mode === "sharpness" ? res.peak_scores.sharpness
                      : res.mode === "readiness" ? res.peak_scores.readiness
                      : `${res.pct_of_ceiling}%`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmtDate(res.target_date)} · {res.pct_of_ceiling}% of their ceiling · {res.horizon_days} days out
                  </div>
                </>
              ) : (
                <>
                  <div className="font-heading text-4xl font-black tabular-nums">{res.days_to_peak}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(res.peak_date)} · holds {res.pct_of_ceiling}% of their ceiling</div>
                </>
              )}
            </div>
            <div className="border border-border p-4" data-testid="peak-search-meta">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Search</div>
              <div className="font-heading text-4xl font-black tabular-nums">
                {(res.screened ? res.screened : res.evaluations).toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {res.screened ? `plans screened · ${res.evaluations.toLocaleString()} fully simulated` : "plans simulated"} · {res.depth_label} · {res.elapsed_sec}s · model confidence {res.learning?.overall_confidence}%
              </div>
            </div>
            <div className="border border-border p-4" data-testid="peak-risk">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Injury risk on this plan</div>
              <div className="font-heading text-4xl font-black tabular-nums">{res.mean_risk}<span className="text-base text-muted-foreground">%</span></div>
              <div className="text-xs text-muted-foreground">average · worst day {res.worst_risk}%</div>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm mb-2">Personal ceiling</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {SCORES.map((k) => <CeilingCard key={k} k={k} c={res.ceiling[k]} />)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Ceilings are the asymptote of this athlete's own adaptation curve, capped at 98. Effort's ceiling is the
              highest effort they have sustained <b>without</b> wrecking their rest.
            </p>
          </div>

          <div className="border border-border" data-testid="peak-vs-current">
            <div className="px-4 py-2.5 border-b border-border font-heading font-bold text-sm">
              {res.mode_label} plan vs the current calendar{res.target_day ? ` · on ${fmtDate(res.target_date)}` : ""}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr><th className="text-left px-4 py-2">Score</th><th className="text-right px-3 py-2">Current plan</th><th className="text-right px-3 py-2">{res.mode_label}</th><th className="text-right px-3 py-2">Δ</th><th className="text-right px-4 py-2">Ceiling</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {SCORES.map((k) => {
                    const cur = res.current_plan?.final_scores?.[k];
                    const nxt = res.peak_scores?.[k];
                    const delta = cur != null && nxt != null ? +(nxt - cur).toFixed(1) : null;
                    return (
                      <tr key={k} data-testid={`peak-cmp-${k}`}>
                        <td className="px-4 py-2 capitalize">{k}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{cur ?? "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">{nxt ?? "—"}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${delta > 0 ? "text-traffic-green" : delta < 0 ? "text-traffic-red" : ""}`}>{delta != null ? (delta > 0 ? `+${delta}` : delta) : "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{res.ceiling[k].ceiling}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t border-border text-xs text-muted-foreground">
              {res.current_plan?.note} Current calendar has {res.current_plan?.sessions} planned session(s) ahead · average injury risk {res.current_plan?.mean_risk}%.
            </div>
          </div>

          <div className="border border-border" data-testid="peak-trajectory">
            <div className="px-4 py-2.5 border-b border-border font-heading font-bold text-sm">
              Projected readiness · {res.mode_label} plan vs current
            </div>
            <div className="h-56 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} minTickGap={24} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="peak" name={`${res.mode_label} plan`} stroke="#0a0a0a" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="current" name="Current plan" stroke="#9ca3af" dot={false} strokeWidth={2} strokeDasharray="4 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-border" data-testid="peak-week">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold text-sm flex-1">The winning week · {res.plan.sessions_per_week} sessions/week, {res.plan.rest_days} rest day(s), {res.plan.taper_days}-day taper</span>
              <button data-testid="peak-toggle-diff" onClick={() => setShowDiff((s) => !s)} className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">
                {showDiff ? "Hide" : "Show"} every change ({res.diff?.days_changed} days)
              </button>
              <button data-testid="peak-save-run" onClick={saveRun}
                title="Keep this run · reopening it later costs no credits"
                className="text-xs border border-foreground px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" /> Save this run
              </button>
              <button data-testid="peak-apply" onClick={() => apply()} disabled={applying}
                className="text-xs px-3 py-1.5 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center gap-2">
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {armed ? `Confirm · write ${res.plan_task_count ?? ""} sessions to the calendar` : "Apply to calendar"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-border">
              {res.plan.weekly.map((day) => (
                <div key={day.dow} className="p-3 min-h-[110px]" data-testid={`peak-day-${day.day}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{day.day}</div>
                  {day.sessions.length === 0 ? <div className="text-xs text-muted-foreground mt-2">Rest</div> :
                    day.sessions.map((s, i) => (
                      <div key={i} className="mt-2 text-xs">
                        <div className="font-medium leading-tight">{s.title}</div>
                        <div className="text-muted-foreground">{String(s.hour).padStart(2, "0")}:00 · {s.intensity} · {s.duration_min}m</div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {showDiff && (
            <div className="border border-border" data-testid="peak-diff">
              <div className="px-4 py-2.5 border-b border-border font-heading font-bold text-sm">
                Comprehensive change list · {res.diff.days_changed} of the next 28 days change · {res.diff.sessions_added} added, {res.diff.sessions_removed} removed
                <span className="text-muted-foreground font-normal"> ({res.diff.sessions_before} → {res.diff.sessions_after} sessions over the horizon)</span>
              </div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border sticky top-0 bg-background">
                    <tr><th className="text-left px-4 py-2">Day</th><th className="text-left px-3 py-2">Currently</th><th className="text-left px-3 py-2">Peak plan</th><th className="text-right px-4 py-2">Load</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {res.diff.days.map((row) => (
                      <tr key={row.date} className={row.changed ? "" : "text-muted-foreground"} data-testid={`peak-diff-${row.date}`}>
                        <td className="px-4 py-2 whitespace-nowrap">{fmtDate(row.date)}</td>
                        <td className="px-3 py-2">{row.before.length ? row.before.map((t) => t.title).join(", ") : <span className="text-muted-foreground">nothing planned</span>}</td>
                        <td className="px-3 py-2">{row.after.length ? row.after.map((t) => `${t.title}${t.hour != null ? ` @${String(t.hour).padStart(2, "0")}:00` : ""} (${t.intensity})`).join(", ") : <span className="text-muted-foreground">rest</span>}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{row.load_before} → {row.load_after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="border border-border" data-testid="peak-drivers">
            <div className="px-4 py-2.5 border-b border-border font-heading font-bold text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Why this plan · learned weights
            </div>
            <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <tr><th className="text-left px-4 py-2">Score</th><th className="text-left px-3 py-2">Driver</th><th className="text-right px-3 py-2">Weight</th><th className="text-right px-4 py-2">Conf · n</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {res.drivers.weights.map((w, i) => (
                      <tr key={i} data-testid={`peak-weight-${i}`}>
                        <td className="px-4 py-2 capitalize">{w.score}</td>
                        <td className="px-3 py-2">{w.factor}<div className="text-[10px] text-muted-foreground">{w.source}</div></td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${w.weight > 0 ? "text-traffic-green" : "text-traffic-red"}`}>{w.weight > 0 ? "+" : ""}{w.weight}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{w.confidence}% · {w.samples}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Activities the search kept</div>
                  <ul className="text-sm space-y-1">
                    {res.drivers.chosen.map((c, i) => (
                      <li key={i} className="flex justify-between gap-3" data-testid={`peak-chosen-${i}`}>
                        <span>{c.title} <span className="text-muted-foreground">· {c.task_class}</span></span>
                        <span className="tabular-nums text-muted-foreground whitespace-nowrap">{c.per_week}×/wk · {c.slot} · {c.intensity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {res.drivers.best_time_of_day && (
                  <p className="text-sm" data-testid="peak-best-tod">
                    Best training slot from their own data: <b>{res.drivers.best_time_of_day}</b>
                    <span className="text-muted-foreground"> (measured by next-day rest)</span>
                  </p>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Frequency sweet spots</div>
                  <ul className="text-sm space-y-0.5">
                    {Object.entries(res.drivers.frequency || {}).filter(([, v]) => v.best_per_week != null).map(([cls, v]) => (
                      <li key={cls} className="flex justify-between"><span className="capitalize">{cls}</span><span className="tabular-nums">{v.best_per_week}×/week</span></li>
                    ))}
                  </ul>
                </div>
                <p className="text-[11px] text-muted-foreground">{res.learning?.note}</p>
              </div>
            </div>
          </div>

          {res.limits?.length > 0 && (
            <div className="border-l-2 border-traffic-amber pl-3 text-xs text-muted-foreground space-y-1" data-testid="peak-limits">
              <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-traffic-amber flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> What holds this back</div>
              {res.limits.map((l, i) => <div key={i}>· {l}</div>)}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Model projection from this athlete's own data · not a clinical prediction and not a promise. Ranges widen the
            further out the projection runs and the thinner the data is.
          </p>
        </div>
      )}

      {!running && errors.length > 0 && (
        <div className="p-4 text-xs text-traffic-red" data-testid="peak-errors">
          {errors.map((e, i) => <div key={i}>{e.athlete_name || e.athlete_id}: {e.error}</div>)}
        </div>
      )}

      {!running && !job && (
        <div className="p-6 text-sm text-muted-foreground" data-testid="peak-empty">
          Pick a compute budget and run it. The engine fits this athlete's own response weights (task class, activity,
          time of day, weekly frequency, recovery-after-hard), estimates the ceiling their adaptation curve is heading
          for, then searches thousands of weekly plans for the fastest safe route there.
        </div>
      )}
    </div>
  );
}
